import { useSyncExternalStore } from 'react';
import { ACCENT_KEY, DEFAULT_ACCENT_COLOR, accentPreset, isAccentColor, hexRgb, onAccent, accentAction } from './accents';

/** ============ Темы ============ */
export const THEME_LIGHT = {
  bg: "#f6f7fb",
  bgCard: "#ffffff",
  bgSoft: "#f2f4f8",
  text: "#0b1220",
  textSoft: "#5b6474",
  // Revert to original brand color
  brand: "#1e66f5",
  brandAlt: "#06b6d4",   // cyan-500
  danger: "#d11a2a",
  border: "#e6e8ef",
  // Deep, smeared shadow for calm depth
  shadow: "0 24px 64px rgba(2,6,23,0.12), 0 8px 24px rgba(2,6,23,0.08)",
  // Glass tokens
  glassBg: "rgba(255,255,255,0.6)",
  glassBorder: "rgba(15,23,42,0.08)",
  glassBlur: "12px",
  // Soft orbital light
  gradBg: "radial-gradient(1200px 800px at -10% -12%, rgba(79,70,229,0.06) 0%, rgba(246,247,251,0) 45%), linear-gradient(180deg, #ffffff 0%, #f6f7fb 100%)",
};

export const THEME_DARK = {
  bg: "#0b1220",
  bgCard: "#0f172a",
  bgSoft: "#0b132b",
  text: "#e5e7eb",
  textSoft: "#94a3b8",
  // Revert to original dark brand
  brand: "#60a5fa",
  brandAlt: "#22d3ee",   // cyan-400
  danger: "#ef4444",
  border: "#1f2937",
  // Deeper smeared shadow in dark
  shadow: "0 30px 80px rgba(0,0,0,.45), 0 12px 32px rgba(0,0,0,.32)",
  glassBg: "rgba(17,24,39,0.6)",
  glassBorder: "rgba(148,163,184,0.08)",
  glassBlur: "14px",
  gradBg: "radial-gradient(1100px 760px at -10% -14%, rgba(37,99,235,0.10) 0%, rgba(11,18,32,0) 50%), linear-gradient(180deg, #0b1220 0%, #0b1220 100%)",
};


// Existing callers can keep using this live object; React views subscribe via useTheme.
export const theme = { ...THEME_LIGHT, mode: 'light' };
export const THEME_KEY = 'ai_diary_theme';
const normalizeMode = value => ['auto', 'light', 'dark'].includes(value) ? value : 'auto';
function readPreference(key, fallback) {
  try { return localStorage.getItem(key) || fallback; } catch { return fallback; }
}
export const __initialTheme = normalizeMode(readPreference(THEME_KEY, 'auto'));
let selectedMode = __initialTheme;
let selectedAccent = accentPreset(readPreference(ACCENT_KEY, DEFAULT_ACCENT_COLOR)).id;
let mediaQuery;
let mediaListenerAttached = false;
let revision = 0;
const subscribers = new Set();
const subscribe = listener => { subscribers.add(listener); return () => subscribers.delete(listener); };
const getSnapshot = () => revision;
export function useTheme() {
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return theme;
}
export const getThemeMode = () => selectedMode;
export const getAccentColor = () => selectedAccent;

function getSystemMode() {
  try {
    if (!mediaQuery) mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    return mediaQuery.matches ? 'dark' : 'light';
  } catch { return 'light'; }
}
function attachAutoListener() {
  try {
    getSystemMode();
    if (!mediaListenerAttached && mediaQuery?.addEventListener) {
      mediaQuery.addEventListener('change', () => {
        if (selectedMode === 'auto') applyTheme('auto');
      });
      mediaListenerAttached = true;
    }
  } catch {}
}
export function applyTheme(mode = selectedMode) {
  selectedMode = normalizeMode(mode);
  const effective = selectedMode === 'auto' ? getSystemMode() : selectedMode;
  const palette = accentPreset(selectedAccent)[effective];
  Object.assign(theme, effective === 'light' ? THEME_LIGHT : THEME_DARK, palette, {
    mode: effective, accent: selectedAccent,
    brandText: onAccent(palette.brand), brandAltText: onAccent(palette.brandAlt),
    action: accentAction(palette.brand, effective), actionAlt: accentAction(palette.brandAlt, effective),
  });
  // CSS tokens also update synchronously, including portals and scrollbars.
  try {
    const root = document.documentElement;
    root.setAttribute('data-theme', effective);
    root.setAttribute('data-accent', selectedAccent);
    const tokens = {
      brand: theme.brand, 'brand-alt': theme.brandAlt,
      'brand-rgb': hexRgb(theme.brand), 'brand-alt-rgb': hexRgb(theme.brandAlt),
      'on-brand': theme.brandText, 'on-brand-alt': theme.brandAltText,
      text: theme.text, muted: theme.textSoft, border: theme.border, background: theme.bg,
      surface: theme.bgCard, 'surface-soft': theme.bgSoft, 'bg-elev': theme.bgCard,
      'bg-soft': theme.bgSoft, 'glass-bg': theme.glassBg, 'glass-border': theme.glassBorder,
      'glass-blur': theme.glassBlur, danger: theme.danger,
    };
    Object.entries(tokens).forEach(([key, value]) => root.style.setProperty('--' + key, value));
    for (const [prefix, action] of [['accent', theme.action], ['accent-alt', theme.actionAlt]]) {
      Object.entries(action).forEach(([key, value]) => root.style.setProperty('--' + prefix + '-' + key, value));
    }
    root.style.colorScheme = effective;
  } catch {}
  if (selectedMode === 'auto') attachAutoListener();
  revision += 1;
  subscribers.forEach(listener => listener());
  try { window.dispatchEvent(new CustomEvent('themechange', { detail: { mode: selectedMode, effective, accent: selectedAccent } })); } catch {}
}
export function setTheme(mode) {
  try { localStorage.setItem(THEME_KEY, normalizeMode(mode)); } catch {}
  applyTheme(mode);
}
export function setAccentColor(accent) {
  if (!isAccentColor(accent)) return false;
  selectedAccent = accent;
  try { localStorage.setItem(ACCENT_KEY, accent); } catch {}
  applyTheme();
  return true;
}
try {
  window.addEventListener('storage', event => {
    if (event.key === ACCENT_KEY || event.key === THEME_KEY || event.key === null) {
      selectedAccent = accentPreset(readPreference(ACCENT_KEY, DEFAULT_ACCENT_COLOR)).id;
      applyTheme(readPreference(THEME_KEY, 'auto'));
    }
  });
} catch {}
applyTheme(__initialTheme);
